// ─────────────────────────────────────────────────────────────────────────────
// DECISION 40: ON A PHONE, THE AIDEY POPUP NEVER OPENS BY ITSELF OVER A PAGE.
//
// Three things used to open the chat with no tap: a meeting starting within five
// minutes (App.js, polled every minute), a high or urgent notification (App.js), and
// a screen's first-visit chat intro (tours/useChatIntro.js). On a phone the popup is
// full screen, so each of them covered the page the person was using.
//
// Every one of them now goes through this file. On a phone:
//   - the meeting and the notification become a toast whose "Open" button is the tap
//     that opens the chat (deliverUnasked);
//   - the intro waits, unrecorded, until the person next opens the chat themselves
//     (queueChatIntro / takeQueuedChatIntro). It is not lost and not marked seen.
// On a tablet or desktop they open the popup as before.
//
// "Phone" is read from matchMedia AT THE MOMENT OF DELIVERY, never from React state:
// the App listeners that call this were registered once, and a breakpoint captured
// then would be stale after a rotation or a resize.
// ─────────────────────────────────────────────────────────────────────────────
import { showToast } from './toast';

export const PHONE_QUERY = '(max-width: 767px)';

export function isPhoneViewport(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win || typeof win.matchMedia !== 'function') return false;
  return !!win.matchMedia(PHONE_QUERY).matches;
}

// open: what the old code did (set the message, open the popup). toastMessage: what the
// person reads on a phone instead. Returns 'opened' or 'toast' so callers and tests can see
// which door was taken.
// actionLabel: the toast button's words ("Open"; decision 76's meeting summary says "Summarise").
export function deliverUnasked({ phone, open, toastMessage, actionLabel = 'Open', toast = showToast }) {
  if (phone) {
    toast(toastMessage, { type: 'info', ms: 12000, action: { label: actionLabel, onClick: open } });
    return 'toast';
  }
  open();
  return 'opened';
}

// ── The waiting intro ────────────────────────────────────────────────────────
// One slot: the first intro to wait keeps it. A later screen's intro is not recorded
// either, so it fires again on a later visit once this one has been shown (and the
// three-intro cap is re-checked then, by decideIntro).
let waiting = null;

// entry: { tourId, body, record } where record() marks the intro seen. Called only on
// delivery, never when it is queued.
export function queueChatIntro(entry) {
  if (!entry || !entry.body) return false;
  if (waiting) return waiting.tourId === entry.tourId;
  waiting = entry;
  return true;
}

export function peekQueuedChatIntro() { return waiting; }

// Hands the waiting intro over and clears the slot. The caller shows it, then calls record().
export function takeQueuedChatIntro() {
  const e = waiting;
  waiting = null;
  return e;
}
